import { FC, useCallback, useState } from "react";
import { P1 } from "../atoms/Paragraph";
import { HeadingSize, H2 } from "../atoms/Heading";
import styled from "styled-components";
import { color } from "@styles";
import { StandardSection } from "../templates/StandardSection";
import { CheckedImage } from "../atoms/CheckedImage";
import { CrossImage } from "../atoms/CrossImage";
import { mobile, tablet } from "components/templates/Breakpoints";

interface Props {
  id?: string;
  comparison: string[];
}

export const ComparisonTableBulletPoints: FC<React.PropsWithChildren<Props>> = ({
  id,
  comparison,
}) => {
  return (
    <Container id={id} aria-label="Comparison Table">
      <StandardSection>
        <H2 size={HeadingSize.H1} align="center" fontSize="3.5rem" lineheight="4rem" pb="4rem">
          More than just insurance
        </H2>
        <ComparisonSection>
          <OldBlock>
            <BeforeHeader>
              <P1 align="center" fontSize="1.5rem" bold pb="1.5rem">
                Traditional Group Insurance
              </P1>
            </BeforeHeader>
            {comparison.map((before, index) => (
              <BeforeBody
                lastRow={index === comparison.length - 1}
                firstRow={index === 0}
                first3Row={index < 3}
                key={before}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconContainer>
                    {index < 3 ? <CheckedImage color={color.B1100} /> : <CrossImage />}
                  </IconContainer>
                  {before}
                </div>
              </BeforeBody>
            ))}
          </OldBlock>
          <NewBlock>
            <AfterHeader>
              <P1 align="center" fontSize="1.5rem" bold color="white" pb="1.5rem">
                Group Insurance through YuLife
              </P1>
              <YugiLookingLeft src="/static/pages/group-insurance/yugi-looking-left.svg" alt="" />
            </AfterHeader>
            {comparison.map((after, index) => (
              <AfterBody
                key={after}
                lastRow={index === comparison.length - 1}
                firstRow={index === 0}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconContainer>
                    <CheckedImage color={color.S1100} />
                  </IconContainer>
                  {after}
                </div>
              </AfterBody>
            ))}
          </NewBlock>
        </ComparisonSection>
      </StandardSection>
    </Container>
  );
};

const ComparisonSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const Block = styled.div`
  width: 100%;
`;

const OldBlock = styled(Block)`
  border: 2px solid ${color.B230};
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
`;
const NewBlock = styled(Block)`
  border: 2px solid ${color.S3100};
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const Container = styled.section`
  background: ${color.B210};
`;

const customTextSelection = `
  ::-moz-selection {
    background: ${color.P20};
  }
  ::selection {
    background: ${color.P20};
  }
`;

const BeforeBody = styled.div<{ lastRow?: boolean; firstRow?: boolean; first3Row?: boolean }>`
  color: ${(p) => (p.first3Row ? color.B1100 : color.B2100)};
  padding: 0.9375rem 0;
  padding-left: 3.8rem;
  padding-right: 3.8rem;
  font-weight: bold;
  padding-top: ${(p) => (p.firstRow ? "3.8rem" : "none")};
  padding-bottom: ${(p) => (p.lastRow ? "3.8rem" : "none")};

  ${mobile} {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: ${(p) => (p.firstRow ? "1.5rem" : "none")};
    padding-bottom: ${(p) => (p.lastRow ? "1.5rem" : "none")};
  }
`;

const AfterBody = styled.div<{ lastRow?: boolean; firstRow?: boolean }>`
  color: ${color.B1100};
  font-weight: bold;
  padding: 0.9375rem 0;
  padding-left: 3.8rem;
  padding-right: 3.8rem;
  padding-top: ${(p) => (p.firstRow ? "3.8rem" : "none")};
  padding-bottom: ${(p) => (p.lastRow ? "3.8rem" : "none")};

  ${mobile} {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: ${(p) => (p.firstRow ? "1.5rem" : "none")};
    padding-bottom: ${(p) => (p.lastRow ? "1.5rem" : "none")};
  }
`;

const BeforeHeader = styled.div`
  background: ${color.B230};
  color: ${color.B2100};
  border-top-left-radius: 5px;
`;

const AfterHeader = styled.div`
  position: relative;
  background: ${color.S3100};
  color: ${color.white};
  border-top-right-radius: 5px;
`;

const YugiLookingLeft = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 4rem;

  ${tablet} {
    display: none;
  }
`;

const IconContainer = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  margin-right: 1.5rem;

  ${mobile} {
    margin-right: 1rem;
  }
`;
